import React, { ReactElement, useEffect, useMemo, useRef } from 'react'
import { Flex, Text } from 'theme-ui'
import { Svg, SimpleDropdown, SimpleActionMenuItemProps } from 'ui'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import useQueryString from 'utils/useQueryString'
import { setSelectedFeaturedProfile } from 'store/multiprofile/featuredProfiles'
import CheckIcon from 'images/analytics/check.svg'
import { featuredProfilesApi } from 'store/api/featuredProfiles/featuredProfiles'

// these strings match what's returned from the API minus the space + word ' Profile' for ease of using in query parameters
export enum FeaturedProfileName {
  PARENTAL_CONTROLS = 'Kid',
  PRODUCTIVITY = 'Productivity',
  PRIVACY = 'Privacy',
  TELEPORT = 'VPN-Replacement',
  BYPASS_GEOBLOCKS = 'Geo-Unblocking',
}

export default function FeaturedProfilesDropdown(): ReactElement {
  const selectedProductId = useAppSelector(s => s.products.selectedProductPk || '')
  const selectedFeaturedProfile = useAppSelector(s => s.featuredProfiles.selectedFeaturedProfile)
  const { data: featuredProfiles } = featuredProfilesApi.useGetProfilesFeaturedQuery({
    selectedProductId,
  })
  const dispatch = useAppDispatch()
  const hideRef = useRef<() => void>()
  const { qs } = useQueryString()

  useEffect(() => {
    dispatch(
      setSelectedFeaturedProfile(
        featuredProfiles?.find(
          featuredProfile => featuredProfile.name === `${qs.featuredProfile} Profile`,
        ),
      ) || undefined,
    )
  }, [dispatch, featuredProfiles, qs.featuredProfile])

  const blankOption: SimpleActionMenuItemProps[] = useMemo(
    () => [
      {
        isSelected: !selectedFeaturedProfile,
        isBackgroundHighlighted: true,
        ariaLabel: `Blank (Custom) option`,
        buttonContent: <Text>Blank (Custom)</Text>,
        children: (
          <Flex sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text>Blank (Custom)</Text>
            {!selectedFeaturedProfile && (
              <Svg
                svg={CheckIcon}
                sx={{ flexShrink: 0, fill: 'white', opacity: 0.5 }}
                descriptiveText="Selected option check mark"
              />
            )}
          </Flex>
        ),
        onClick: () => {
          dispatch(setSelectedFeaturedProfile(undefined))
          hideRef.current?.()
        },
      },
    ],
    [dispatch, selectedFeaturedProfile],
  )

  const options: SimpleActionMenuItemProps[] = useMemo(
    () =>
      featuredProfiles?.map(featuredProfile => ({
        isSelected: selectedFeaturedProfile?.name === featuredProfile.name,
        isBackgroundHighlighted: true,
        ariaLabel: `${featuredProfile.name} option`,
        buttonContent: <Text>{featuredProfile.name}</Text>,
        children: (
          <Flex sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text>{featuredProfile.name}</Text>
            {featuredProfile.PK === selectedFeaturedProfile?.PK && (
              <Svg
                svg={CheckIcon}
                sx={{ flexShrink: 0, fill: 'white', opacity: 0.5 }}
                descriptiveText="Selected option check mark"
              />
            )}
          </Flex>
        ),
        onClick: () => {
          dispatch(setSelectedFeaturedProfile(featuredProfile))
          hideRef.current?.()
        },
      })) ?? [],
    [dispatch, hideRef, featuredProfiles, selectedFeaturedProfile],
  )

  return (
    <Flex
      sx={{
        width: '100%',
        flexDirection: 'column',
        gap: '0.8rem',
      }}
    >
      <SimpleDropdown
        label="Starting Configuration"
        testId="featured-profiles-dropdown"
        ariaLabel="Select starting configuration"
        items={[...blankOption, ...options]}
        hideRef={hideRef}
        defaultValue="Blank (Custom)"
      />
    </Flex>
  )
}
